/**
 * Flood Form 1 Daily Affected Report Handler (CORRECTED)
 * Follows exact logic from Form-1-daily-report-js.txt
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';
import moment from 'moment';

/**
 * Load logo image
 */
const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

/**
 * Format date to DD/MM/YYYY
 */
const formatDateToDDMMYYYY = (dateStr) => {
  // If dateStr is null, undefined, or empty string
  if (!dateStr || dateStr === 'undefined' || dateStr === 'null') return '';
  
  // Explicitly tell moment how to parse the incoming date to avoid "Invalid date"
  const parsed = moment.utc(dateStr, ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'], true);
  
  // Fallback: if parsing fails, return the string as-is or an empty string
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
};

/**
 * Generate IST filename with date range
 */
/**
 * Generate IST filename with date range
 * FIX: Added strict format array to prevent "Invalid date"
 */
const generateISTFilenameWithRange = (prefix, fromDate, toDate) => {
  const formats = ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'];
  
  // Use UTC and strict formats just like the display function
  const fromFormatted = moment.utc(fromDate, formats).format('YYMMDD');
  const toFormatted = moment.utc(toDate, formats).format('YYMMDD');

  const now = new Date();
  now.setMinutes(now.getMinutes() + 330); // IST offset
  const istTime = now
    .toISOString()
    .split('T')[1]
    .split('.')[0]
    .replace(/:/g, '');

  return `${prefix}_${fromFormatted}_${toFormatted}_${istTime}`;
};

/**
 * MAIN HANDLER: Flood Form 1 Daily Affected Report (PDF)
 * EXACT LOGIC FROM REFERENCE FILE
 */
export const handleFloodDailyPartiallyAffectedReportPDF = async (
  message,
  format,
  rawData
) => {
  console.log("🌊 Flood Daily Partially Affected Report PDF generation started");

  if (format !== "pdf") {
    alert("Flood Daily Partially Affected supports PDF format");
    return;
  }

  try {
    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const centerX = pageWidth / 2;

    // Extract dates
    const fromDate =
      message?.debug?.tool_result?.parameters?.from_date ||
      message?.debug?.parameters?.fromdate ||
      rawData?.fromdate;

    let toDate =
      message?.debug?.tool_result?.parameters?.to_date ||
      message?.debug?.parameters?.todate ||
      rawData?.todate;

    if (!toDate) {
      toDate = new Date().toISOString().split("T")[0];
    }

    const toDateDisplay = formatDateToDDMMYYYY(toDate);

    // Load logo - EXACT POSITIONING FROM REFERENCE
    let logoImg = null;
    try {
      logoImg = await loadLogo(govtLogo);
      if (logoImg) {
        doc.addImage(logoImg, "png", 270, 55, 0, 0); // Exact from reference
      }
    } catch (e) {
      console.warn("⚠️ Logo load failed:", e.message);
    }

    // Header - EXACT TEXT AND POSITIONS FROM REFERENCE
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 130, { align: 'center' });
    doc.text('Department Name', centerX, 150, { align: 'center' });
    doc.text(
      `Flood Daily Partially Report (Dated on ${toDate})`,
      centerX,
      170,
      { align: "center" }
    );

    // Extract district name
    let districtName = "[DISTRICT]";
    if (rawData?.districtname) {
      districtName = String(rawData.districtname).trim().toUpperCase();
    } else if (rawData?.district_name) {
      districtName = String(rawData.district_name).trim().toUpperCase();
    }

    // Helper functions
    const extractArrayOrObject = (data) => {
      if (!data) return {};
      if (Array.isArray(data) && data.length > 0) return data[0];
      if (!Array.isArray(data)) return data;
      return {};
    };

    const safeVal = (value) => {
      if (value === null || value === undefined || value === "") return "";
      return String(value);
    };

    // Extract all data sections with CORRECT field names
    const blockaffected = rawData?.blockaffected || rawData?.block_affected || [];
    const floodRiver = rawData?.floodrivermap || rawData?.flood_river_map || [];
    const geoAffected = extractArrayOrObject(rawData?.drgeoaffected || rawData?.dr_geo_affected);
    const floodImpact = extractArrayOrObject(rawData?.drfloodimpact || rawData?.dr_flood_impact);
    const popAffected = extractArrayOrObject(rawData?.drpopulationaffected || rawData?.dr_population_affected);
    const areaAffected = extractArrayOrObject(rawData?.drareaaffected || rawData?.dr_area_affected);

    // FIX: Correct field names for damaged house (animal shed)
    const damagedHouse = extractArrayOrObject(rawData?.drdamagedhouse || rawData?.dr_damaged_house);

    const fishingGround = extractArrayOrObject(rawData?.drfishingground || rawData?.dr_fishing_ground);

    // FIX: Correct field names for deaths
    const totalDeaths = extractArrayOrObject(rawData?.drtotaldeath || rawData?.dr_total_death);

    const totalBoats = extractArrayOrObject(rawData?.drtotalboats || rawData?.dr_total_boats);

    // FIX: Correct field names for evacuation
    const popEvacuation = extractArrayOrObject(rawData?.drpopulationevacuation || rawData?.dr_population_evacuation);

    // FIX: Correct field names for flood relief
    const floodRelief = extractArrayOrObject(rawData?.drfloodrelief || rawData?.dr_flood_relief);

    // FIX: Correct field names for community kitchen
    const communityKitchen = extractArrayOrObject(rawData?.drcommunitykitchen || rawData?.dr_community_kitchen);

    const healthCenter = extractArrayOrObject(rawData?.drhealthcenter || rawData?.dr_health_center);
    // const animalCamp = extractArrayOrObject(rawData?.dranimalcamp || rawData?.dr_animal_camp);
    const reliefDistribution = extractArrayOrObject(rawData?.drreliefdistribution || rawData?.dr_relief_distribution);

    // Extract block data
    let blockAffectedCount = 0;
    let blockAffectedList = "";

    if (Array.isArray(blockaffected) && blockaffected.length > 0) {
      const blockListStr =
        blockaffected[0]?.blockaffectedlist ||
        blockaffected[0]?.block_affected_list ||
        "";
      blockAffectedList = blockListStr;
      if (blockListStr) {
        blockAffectedCount = blockListStr.split(",").filter((b) => b.trim()).length;
      }
    }

    // Extract river list
    let riverList = "";
    if (Array.isArray(floodRiver) && floodRiver.length > 0) {
      riverList = floodRiver
        .map((r) => safeVal(r?.river_list || r?.river_name || r?.rivername))
        .filter((n) => n)
        .join(", ");
    } else if (floodRiver?.river_list) {
      riverList = safeVal(floodRiver.river_list);
    }

    console.log("✅ Data extraction completed");

    // ═══════════════════════════════════════════════════════════════════════════
    // CREATE HTML TABLE (EXACT STRUCTURE FROM REFERENCE)
    // ═══════════════════════════════════════════════════════════════════════════

    const tableHTML = `
      <table id="report_table" border="2">
        <tbody>
          <!-- Header Row -->
          <tr>
            <td colspan="1"><b><span>Sl No.</span></b></td>
            <td colspan="11"><b><span>District: ${districtName}</span></b></td>
          </tr>

          <!-- Section 1: Blocks -->
          <tr>
            <td rowspan="2"><span>1</span></td>
            <td colspan="5"><b>Total Number of blocks partially affected so far (Cumulative)</b></td>
            <td colspan="10">${safeVal(blockAffectedCount)}</td>
          </tr>
          <tr>
            <td colspan="5"><b>Names of the blocks partially affected so far (Cumulative)</b></td>
            <td colspan="10">${safeVal(blockAffectedList)}</td>
          </tr>

          <!-- Section 2: Rural Area -->
          <tr>
            <td rowspan="2"><span>2</span></td>
            <td rowspan="2"><b>Rural area</b></td>
            <td colspan="7">Total number of Gram Panchayats affected so far (Cumulative)</td>
            <td colspan="10">${safeVal(geoAffected.totl_grm_pnchyt)}</td>
          </tr>
          <tr>
            <td colspan="7">Number of villages affected so far (Cumulative)</td>
            <td colspan="10">${safeVal(geoAffected.nos_vilge_aftd)}</td>
          </tr>

          <!-- Section 3: Urban Area -->
          <tr>
            <td rowspan="5"><span>3</span></td>
            <td rowspan="5"><b>Urban Area</b></td>
            <td colspan="7">Total number of Nagar Panchayats affected so far (Cumulative)</td>
            <td colspan="10">${safeVal(geoAffected.totl_ngr_pnchyt)}</td>
          </tr>
          <tr>
            <td colspan="7">Total affected Municipal corporation (Cumulative)</td>
            <td colspan="10">${safeVal(geoAffected.totl_mucpl_corp)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of affected Nagar Parishad (Cumulative)</td>
            <td colspan="10">${safeVal(geoAffected.totl_ngr_parsad)}</td>
          </tr>
          <tr>
            <td colspan="7">Number of wards affected so far (Cumulative)</td>
            <td colspan="10">${safeVal(geoAffected.nos_wrd_aftd)}</td>
          </tr>
          <tr>
            <td colspan="7">Total</td>
            <td colspan="10">${safeVal(geoAffected.totl_aftd)}</td>
          </tr>

          <!-- Section 4: Population Affected (Complex Header) -->
          <tr>
            <td rowspan="5"><span>4</span></td>
            <td rowspan="2"></td>
            <td colspan="3" style="text-align:center"><b>Until Yesterday</b></td>
            <td colspan="3" style="text-align:center"><b>Today (Newly affected)</b></td>
            <td colspan="3" style="text-align:center"><b>Population affected so far (Cumulative)</b></td>
          </tr>
          <tr>
            <td>Rural area</td>
            <td>Urban area</td>
            <td>Total</td>
            <td>Rural area</td>
            <td>Urban area</td>
            <td>Total</td>
            <td>Rural area</td>
            <td>Urban area</td>
            <td>Total</td>
          </tr>
          <tr>
            <td>A. <b>Affected population</b></td>
            <td>${safeVal(popAffected.aftd_pop_rural_ystd)}</td>
            <td>${safeVal(popAffected.aftd_pop_urban_ystd)}</td>
            <td>${safeVal(popAffected.aftd_pop_totl_ystd)}</td>
            <td>${safeVal(popAffected.aftd_pop_rural_today)}</td>
            <td>${safeVal(popAffected.aftd_pop_urban_today)}</td>
            <td>${safeVal(popAffected.aftd_pop_totl_today)}</td>
            <td>${safeVal(popAffected.aftd_pop_rural)}</td>
            <td>${safeVal(popAffected.aftd_pop_urban)}</td>
            <td>${safeVal(popAffected.totl_pop_aftd)}</td>
          </tr>
          <tr>
            <td>B. <b>Number of families affected</b></td>
            <td>${safeVal(popAffected.aftd_fam_rural_ystd)}</td>
            <td>${safeVal(popAffected.aftd_fam_urban_ystd)}</td>
            <td>${safeVal(popAffected.aftd_fam_totl_ystd)}</td>
            <td>${safeVal(popAffected.aftd_fam_rural_today)}</td>
            <td>${safeVal(popAffected.aftd_fam_urban_today)}</td>
            <td>${safeVal(popAffected.aftd_fam_totl_today)}</td>
            <td>${safeVal(popAffected.aftd_fam_rural)}</td>
            <td>${safeVal(popAffected.aftd_fam_urban)}</td>
            <td>${safeVal(popAffected.totl_fam_aftd)}</td>
          </tr>
          <tr>
            <td>C. <b>Number of animals affected</b></td>
            <td>${safeVal(popAffected.aftd_anml_rural_ystd)}</td>
            <td>${safeVal(popAffected.aftd_anml_urban_ystd)}</td>
            <td>${safeVal(popAffected.aftd_anml_totl_ystd)}</td>
            <td>${safeVal(popAffected.aftd_anml_rural_today)}</td>
            <td>${safeVal(popAffected.aftd_anml_urban_today)}</td>
            <td>${safeVal(popAffected.aftd_anml_totl_today)}</td>
            <td>${safeVal(popAffected.aftd_anml_rural)}</td>
            <td>${safeVal(popAffected.aftd_anml_urban)}</td>
            <td>${safeVal(popAffected.totl_anml_aftd)}</td>
          </tr>

          <!-- Section 5: Rivers -->
          <tr>
            <td><span>5</span></td>
            <td colspan="5"><b>Name of major river which caused flood situation</b></td>
            <td colspan="6">${safeVal(riverList)}</td>
          </tr>

          <!-- Section 6: Current Status of Flood -->
          <tr>
            <td rowspan="7"><span>6</span></td>
            <td colspan="11"><b>Current Status of Flood</b></td>
          </tr>
          <tr>
            <td colspan="8">How many blocks are currently affected by floods?</td>
            <td colspan="10">${safeVal(floodImpact.affected_blocks_count)}</td>
          </tr>
          <tr>
            <td colspan="8">How many gram panchayats are currently affected by floods?</td>
            <td colspan="10">${safeVal(floodImpact.affected_gram_panchayats)}</td>
          </tr>
          <tr>
            <td colspan="8">Number of municipal bodies (Nagar Panchayat + Municipal Council + Municipal Corporation) currently affected</td>
            <td colspan="10">${safeVal(floodImpact.affected_municipal_bodies)}</td>
          </tr>
          <tr>
            <td colspan="8">How many villages are currently affected by floods?</td>
            <td colspan="10">${safeVal(floodImpact.affected_villages)}</td>
          </tr>
          <tr>
            <td colspan="8">How many wards are currently affected by floods?</td>
            <td colspan="10">${safeVal(floodImpact.affected_wards)}</td>
          </tr>
          <tr>
            <td colspan="8">How many population is currently affected by floods? (Rural area + Urban area)</td>
            <td colspan="10">${safeVal(floodImpact.affected_population)}</td>
          </tr>

          <!-- Section 7: Area Affected -->
          <tr>
            <td rowspan="6"><span>7</span></td>
            <td colspan="5"><b>Area affected (in hectares)</b></td>
            <td colspan="3"><b>On the Reporting date</b></td>
            <td colspan="6"><b>Till Now (Cumulative)</b></td>
          </tr>
          <tr>
            <td colspan="5">Agricultural</td>
            <td colspan="3">${safeVal(areaAffected.agricultural_area_affected_curr)}</td>
            <td colspan="8">${safeVal(areaAffected.agricultural_area_affected_till_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Non Agricultural land</td>
            <td colspan="3">${safeVal(areaAffected.non_agricultural_area_affected_curr)}</td>
            <td colspan="8">${safeVal(areaAffected.non_agricultural_area_affected_till_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Total Area Affected</td>
            <td colspan="3">${safeVal(areaAffected.total_area_affected_curr)}</td>
            <td colspan="8">${safeVal(areaAffected.total_area_affected_till_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Damaged crop area (in hectares)</td>
            <td colspan="3">${safeVal(areaAffected.damaged_crop_area_affected_curr)}</td>
            <td colspan="8">${safeVal(areaAffected.damaged_crop_area_affected_till_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Estimated value of damaged crops (in Rs lakh)</td>
            <td colspan="3">${safeVal(areaAffected.estimated_value_of_damaged_crops_affected_curr)}</td>
            <td colspan="8">${safeVal(areaAffected.estimated_value_of_damaged_crops_affected_till_now)}</td>
          </tr>

          <!-- Section 8: Houses Damaged -->
          <tr>
            <td rowspan="9"><span>8</span></td>
            <td colspan="5"><b>Number of Houses Damaged</b></td>
            <td colspan="3"><b>On the Reporting date</b></td>
            <td colspan="6"><b>Till Now (Cumulative)</b></td>
          </tr>
          <tr>
            <td rowspan="2"><b>Kutcha Houses</b></td>
            <td colspan="4">Complete</td>
            <td colspan="3">${safeVal(damagedHouse.ktha_cmplt_today)}</td>
            <td colspan="7">${safeVal(damagedHouse.ktha_cmplt)}</td>
          </tr>
          <tr>
            <td colspan="4">Partial</td>
            <td colspan="3">${safeVal(damagedHouse.ktha_prtl_today)}</td>
            <td colspan="6">${safeVal(damagedHouse.ktha_prtl)}</td>
          </tr>
          <tr>
            <td rowspan="2"><b>Concrete Houses</b></td>
            <td colspan="4">Complete</td>
            <td colspan="3">${safeVal(damagedHouse.crct_cmplt_today)}</td>
            <td colspan="7">${safeVal(damagedHouse.crct_cmplt)}</td>
          </tr>
          <tr>
            <td colspan="4">Partial</td>
            <td colspan="3">${safeVal(damagedHouse.crct_prtl_today)}</td>
            <td colspan="6">${safeVal(damagedHouse.crct_prtl)}</td>
          </tr>
          <tr>
            <td colspan="5">Cottage/Hut</td>
            <td colspan="3">${safeVal(damagedHouse.ctg_hut_today)}</td>
            <td colspan="6">${safeVal(damagedHouse.ctg_hut)}</td>
          </tr>
          <tr>
            <td colspan="5">Animal Shed</td>
            <td colspan="3">${safeVal(damagedHouse.anml_shd_today)}</td>
            <td colspan="6">${safeVal(damagedHouse.anml_shd)}</td>
          </tr>
          <tr>
            <td colspan="5">Estimated value of damaged Houses (in Rs lakh)</td>
            <td colspan="3">${safeVal(damagedHouse.estd_hse_val_today)}</td>
            <td colspan="6">${safeVal(damagedHouse.estd_hse_val)}</td>
          </tr>
          <tr>
            <td colspan="5">Estimated value of damaged Public property (in Rs lakh)</td>
            <td colspan="3">${safeVal(damagedHouse.estd_pub_prty_val_today)}</td>
            <td colspan="6">${safeVal(damagedHouse.estd_pub_prty_val)}</td>
          </tr>

          <!-- Section 9: Fishing Grounds -->
          <tr>
            <td rowspan="6"><span>9</span></td>
            <td colspan="5"><b>Fishing grounds</b></td>
            <td colspan="3"><b>On the Reporting date</b></td>
            <td colspan="6"><b>Till Now (Cumulative)</b></td>
          </tr>
          <tr>
            <td rowspan="2"><b>Damaged Boats</b></td>
            <td colspan="4">Complete</td>
            <td colspan="3">${safeVal(fishingGround.damaged_boats_complete_curr)}</td>
            <td colspan="6">${safeVal(fishingGround.damaged_boats_complete_till_now)}</td>
          </tr>
          <tr>
            <td colspan="4">Partial</td>
            <td colspan="3">${safeVal(fishingGround.damaged_boats_partial_curr)}</td>
            <td colspan="6">${safeVal(fishingGround.damaged_boats_partial_till_now)}</td>
          </tr>
          <tr>
            <td rowspan="2"><b>Damaged Net</b></td>
            <td colspan="4">Complete</td>
            <td colspan="3">${safeVal(fishingGround.damaged_net_complete_curr)}</td>
            <td colspan="6">${safeVal(fishingGround.damaged_net_complete_till_now)}</td>
          </tr>
          <tr>
            <td colspan="4">Partial</td>
            <td colspan="3">${safeVal(fishingGround.damaged_net_partial_curr)}</td>
            <td colspan="6">${safeVal(fishingGround.damaged_net_partial_till_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Area of damaged fish egg farm (in hectares)</td>
            <td colspan="3">${safeVal(fishingGround.area_of_damaged_fish_egg_farm_curr)}</td>
            <td colspan="6">${safeVal(fishingGround.area_of_damaged_fish_egg_farm_till_now)}</td>
          </tr>

          <!-- Section 10: Deaths -->
          <tr>
            <td rowspan="3"><span>10</span></td>
            <td colspan="5"><b>Total number of deaths so Far</b></td>
            <td colspan="3"><b>On the Reporting date</b></td>
            <td colspan="6"><b>Till Now (Cumulative)</b></td>
          </tr>
          <tr>
            <td colspan="5">Human</td>
            <td colspan="3">${safeVal(totalDeaths.human_deaths_curr)}</td>
            <td colspan="6">${safeVal(totalDeaths.human_deaths_till_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Animal</td>
            <td colspan="3">${safeVal(totalDeaths.animal_deaths_curr)}</td>
            <td colspan="6">${safeVal(totalDeaths.animal_deaths_till_now)}</td>
          </tr>

          <!-- Section 11: Boats -->
          <tr>
            <td rowspan="4"><span>11</span></td>
            <td colspan="11"><b>Total number of boats/motor boats operated/used</b></td>
          </tr>
          <tr>
            <td colspan="7">Motorboat</td>
            <td colspan="10">${safeVal(totalBoats.mtr_bot)}</td>
          </tr>
          <tr>
            <td colspan="7">Government Boat</td>
            <td colspan="10">${safeVal(totalBoats.govt_bot)}</td>
          </tr>
          <tr>
            <td colspan="7">Private Boat</td>
            <td colspan="10">${safeVal(totalBoats.pvt_bot)}</td>
          </tr>

          <!-- Section 12: Evacuation -->
          <tr>
            <td rowspan="3"><span>12</span></td>
            <td colspan="7">Total number of Population evacuated till yesterday (A)</td>
            <td colspan="10">${safeVal(popEvacuation.totl_evctd_till_ystd)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of Population evacuated today (B)</td>
            <td colspan="10">${safeVal(popEvacuation.totl_evctd_today)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of Population evacuated till now (A+B)</td>
            <td colspan="10">${safeVal(popEvacuation.totl_evctd_till_now)}</td>
          </tr>

          <!-- Section 13: Flood Relief Camps -->
          <tr>
            <td rowspan="8"><span>13</span></td>
            <td colspan="7">Total number of Flood Relief Camps opened so far (I)</td>
            <td colspan="10">${safeVal(floodRelief.totl_cmp_opn)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of Flood Relief Camps closed so far (Cumulative) (II)</td>
            <td colspan="10">${safeVal(floodRelief.totl_cmp_cls)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of Flood Relief Camps Operational today (I-II)</td>
            <td colspan="10">${safeVal(floodRelief.totl_cmp_opn_tdy)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of people registered in Disaster relief centers till Yesterday (A)</td>
            <td colspan="10">${safeVal(floodRelief.totl_ppl_rgtd_til_ystd)}</td>
          </tr>
          <tr>
            <td colspan="7">Number of newly registered people Today (B)</td>
            <td colspan="10">${safeVal(floodRelief.new_ppl_rgtd_tdy)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of people registered so far (A+B)</td>
            <td colspan="10">${safeVal(floodRelief.totl_ppl_rgtd)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of people who have gone back from the camps so far (C)</td>
            <td colspan="10">${safeVal(floodRelief.totl_ppl_gne_bck_fr_cmp)}</td>
          </tr>
          <tr>
            <td colspan="7">Number of people remaining in the camps (A+B-C)</td>
            <td colspan="10">${safeVal(floodRelief.ppl_rmng_in_cmp)}</td>
          </tr>

          <!-- Section 14: Community Kitchen -->
          <tr>
            <td rowspan="8"><span>14</span></td>
            <td colspan="7">Total Number of Community Kitchens opened so far (Cumulative) A (Excluding meal served in flood relief camps)</td>
            <td colspan="10">${safeVal(communityKitchen.totl_cmty_ktch_open)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of community kitchens closed so far (Cumulative) (B)</td>
            <td colspan="10">${safeVal(communityKitchen.totl_cmty_ktch_close)}</td>
          </tr>
          <tr>
            <td colspan="7">Total number of Community Kitchens Operational today (A-B)</td>
            <td colspan="10">${safeVal(communityKitchen.totl_cmty_ktch_open_tdy)}</td>
          </tr>
          <tr>
            <td colspan="11"><b>Number of people eating food in community kitchens (except flood relief camps)</b></td>
          </tr>
          <tr>
            <td colspan="7">Number of people eating lunch today.</td>
            <td colspan="10">${safeVal(communityKitchen.num_of_ppl_etng_lun_tdy)}</td>
          </tr>
          <tr>
            <td colspan="7">Number of people having dinner tonight.</td>
            <td colspan="10">${safeVal(communityKitchen.num_of_ppl_etng_dinr_tngt)}</td>
          </tr>
          <tr>
            <td colspan="7">Total Number of people having meals today</td>
            <td colspan="10">${safeVal(communityKitchen.totl_num_of_ppl_etng_tdy)}</td>
          </tr>
          <tr>
            <td colspan="7"><b>Total number of meals provided tilll date (Cumulative)</b></td>
            <td colspan="10">${safeVal(communityKitchen.totl_num_of_mls_prvd_til_dt)}</td>
          </tr>
          <!-- Section 15: Health Center -->
          <tr>
            <td rowspan="5"><span>15</span></td>
            <td colspan="5"><b>Health Center</b></td>
            <td colspan="3"><b>Daily</b></td>
            <td colspan="6"><b>Till Now (Cumulative)</b></td>
          </tr>
          <tr>
            <td colspan="5">Number of health center being run</td>
            <td colspan="3">${safeVal(healthCenter.num_of_hlt_ctr_rng_dly)}</td>
            <td colspan="6">${safeVal(healthCenter.num_of_hlt_ctr_rng_til_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Number of people treated</td>
            <td colspan="3">${safeVal(healthCenter.num_of_ppl_trtd_dly)}</td>
            <td colspan="6">${safeVal(healthCenter.num_of_ppl_trtd_til_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Number of Halogen tablets distributed</td>
            <td colspan="3">${safeVal(healthCenter.num_of_hlgn_tlbt_dist_dly)}</td>
            <td colspan="6">${safeVal(healthCenter.num_of_hlgn_tlbt_dist_til_now)}</td>
          </tr>
          <tr>
            <td colspan="5">Amount of bleaching powder sprinkled (KG)</td>
            <td colspan="3">${safeVal(healthCenter.amt_of_blhng_pdr_sprk_dly)}</td>
            <td colspan="6">${safeVal(healthCenter.amt_of_blhng_pdr_sprk_til_now)}</td>
          </tr>

          <!-- Section 16: Animal Camps -->
          <tr>
            <td><span>16</span></td>
            <td colspan="5">Number of animal camps</td>
            <td colspan="3">${safeVal(healthCenter.number_of_animal_camps_daily)}</td>
            <td colspan="6">${safeVal(healthCenter.num_of_anml_cmp_til_now)}</td>
          </tr>
          <tr>
            <td></td>
            <td colspan="5">Number of animals treated</td>
            <td colspan="3">${safeVal(healthCenter.num_of_anml_trtd_dly)}</td>
            <td colspan="6">${safeVal(healthCenter.num_of_anml_trtd_til_now)}</td>
          </tr>

          <!-- Relief Distribution Header -->
          <tr>
            <td></td>
            <td colspan="5"><b>Relief Distribution</b></td>
            <td colspan="3"><b>On the Reporting date</b></td>
            <td colspan="6"><b>Till Now (Cumulative)</b></td>
          </tr>

          <!-- Section 17-21: Relief Distribution -->
          <tr>
            <td><span>17</span></td>
            <td colspan="5">Amount of GR (Gratuitous Relief) recommendation @ Rs. 7000 per family</td>
            <td colspan="3">${safeVal(reliefDistribution.amt_of_gr_rcmdt_tdy)}</td>
            <td colspan="6">${safeVal(reliefDistribution.amt_of_gr_rcmdt_til_now)}</td>
          </tr>
          <tr>
            <td><span>18</span></td>
            <td colspan="5">Number of Dry Ration Packets per family (Chana, Chura, Jaggery, Sattu, Salt, Sugar, etc.)</td>
            <td colspan="3">${safeVal(reliefDistribution.num_of_dry_rtn_pkt_per_fam_tdy)}</td>
            <td colspan="6">${safeVal(reliefDistribution.num_of_dry_rtn_pkt_per_fam_til_now)}</td>
          </tr>
          <tr>
            <td><span>19</span></td>
            <td colspan="5">Number of food packets Rice, Pulses, potatoes, soyabean, Turmeric, etc.</td>
            <td colspan="3">${safeVal(reliefDistribution.num_of_fd_pkt_tdy)}</td>
            <td colspan="6">${safeVal(reliefDistribution.num_of_fd_pkt_til_now)}</td>
          </tr>
          <tr>
            <td><span>20</span></td>
            <td colspan="5">Number of Polythene sheets distributed</td>
            <td colspan="3">${safeVal(reliefDistribution.num_of_poly_shts_dist_tdy)}</td>
            <td colspan="6">${safeVal(reliefDistribution.num_of_poly_shts_dist_til_now)}</td>
          </tr>
          <tr>
            <td><span>21</span></td>
            <td colspan="5">Other Ingredients (KG/Ltr/number)</td>
            <td colspan="3">${safeVal(reliefDistribution.otr_ingts_tdy)}</td>
            <td colspan="6">${safeVal(reliefDistribution.otr_ingts_til_now)}</td>
          </tr>
        </tbody>
      </table>
    `;

    // Parse HTML table
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = tableHTML;
    const table = tempDiv.querySelector('#report_table');

    // Generate PDF with autoTable - EXACT SETTINGS FROM REFERENCE
    autoTable(doc, {
      html: table,
      startX: 10,
      startY: 200,
      theme: 'grid',
      rowPageBreak: 'avoid',
      bodyStyles: { lineColor: [0, 0, 0] },
      columnStyles: {
        2: { cellWidth: 47 },
        3: { cellWidth: 47 },
        4: { cellWidth: 47 },
        5: { cellWidth: 47 },
        6: { cellWidth: 47 },
        7: { cellWidth: 47 },
        8: { cellWidth: 47 },
        9: { cellWidth: 47 },
        10: { cellWidth: 47 },
        11: { cellWidth: 0 },
      },
      didParseCell: function (data) {
        if (data.cell.raw) {
          if (data.cell.raw.querySelector('b')) {
            data.cell.styles.textColor = 'red';
          }
          if (data.cell.raw.querySelector('span')) {
            data.cell.styles.halign = 'center';
          }
        }
      },
      styles: {
        font: 'Helvetica',
        fontSize: 9,
        cellWidth: 'auto',
        halign: 'left',
        cellPadding: 3,
      },
    });

    // Footer - EXACT FROM REFERENCE
    let finalY = doc.lastAutoTable.finalY;
    doc.setFontSize(10);
    doc.text(450, finalY + 80, 'District Magistrate');
    doc.text(450, finalY + 100, `[${districtName}]`);

    // Save with EXACT filename format from reference
    const finalFilename = generateISTFilenameWithRange('partiallyaffecteddfr', fromDate, toDate) + '.pdf';
    doc.save(finalFilename);

    console.log("✅ Flood Daily Partially Affected PDF generated successfully");

  } catch (error) {
    console.error("❌ Flood Daily Partially Affected PDF generation failed");
    console.error("Error:", error.message);
    alert(`PDF Generation Error: ${error.message}`);
  }
};

